/** A split of a whole, as a ring with the headline in the middle and a legend that carries the figures.
 *
 * Slices are drawn from the values handed in; the centre text and each slice's text are the caller's
 * (the server's total is printed, not the sum of the slices). The legend is a list of every slice
 * with its swatch, label, figure and share, so it is the text alternative: no second hidden table.
 * Each arc is focusable with the full sentence as its name, and focusing or hovering an arc or its
 * legend row highlights it and dims the rest. The ring fades and scales in once and is still under
 * reduced motion.
 *
 * **Dashboard band behaviour (ADR-0212, P7).** The active segment is pulled out a few pixels along
 * its middle angle, and the centre swaps from the caller's headline to that segment's figure and
 * name, then back. The legend rows are toggles (`aria-pressed`): switching a segment off removes its
 * arc so the others can be read, and the centre keeps the caller's total with a note of how many are
 * hidden (the chart never sums). A segment with a `receipt` opens it on click or Enter when the
 * caller passes `onOpenReceipt`. Under reduced motion the pull-out and the dimming are instant.
 *
 * **Use a ring only when it earns one.** A split into fewer than three segments is a 100% bar:
 * `StackedShare`. Legend toggles are opt-in (`toggleable`), so a legend that was text stays text.
 * **No number is wider than the hole**: the centre text shrinks to fit the hole it sits in, so a
 * swapped-in figure such as `1,686.5 t` never touches the ring. Inside a `ChartCard` the Table
 * button swaps the ring for the same figures as a table.
 */

import { Group } from "@visx/group";
import { Pie } from "@visx/shape";
import { motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import type { KeyboardEvent } from "react";

import styles from "./Donut.module.css";
import { colourOf } from "./chartTokens";
import type { ChartColour } from "./chartTokens";
import { canOpen, receiptProps } from "./receipt";
import type { OpenReceipt } from "./receipt";
import { ChartTable } from "./ChartTable";
import { useChartAsTable } from "./chartView";

export interface DonutSlice {
  key: string;
  label: string;
  /** Drives the arc's angle. */
  value: number;
  /** The figure as printed: `1,686.5 t`. */
  text: string;
  /** The share as printed: `69.6%`. */
  share?: string;
  colour: ChartColour;
  /** Where this slice's receipt is. A slice opens it only with the chart's `onOpenReceipt`. */
  receipt?: string;
}

const PULL = 5;

export function Donut({
  slices,
  centre,
  centreLabel,
  caption,
  size = 140,
  testId = "donut",
  onOpenReceipt,
  toggleable = false,
}: {
  slices: readonly DonutSlice[];
  /** The headline figure, printed in the middle: `2,423`. */
  centre: string;
  /** Its unit or name: `tCO2e`. */
  centreLabel?: string;
  /** What the ring is: its accessible group name. */
  caption: string;
  size?: number;
  testId?: string;
  onOpenReceipt?: OpenReceipt;
  /** Legend rows become toggles that hide a segment (see the header). Off by default. */
  toggleable?: boolean;
}) {
  const reduce = useReducedMotion();
  const asTable = useChartAsTable();
  const [active, setActive] = useState<string | null>(null);
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const radius = size / 2;
  const sentence = (s: DonutSlice) => `${s.label}: ${s.text}${s.share ? `, ${s.share}` : ""}`;

  const toggle = (key: string) => {
    setOff((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setActive((now) => (now === key ? null : now));
  };
  const shown = slices.filter((s) => s.value > 0 && !off.has(s.key));
  const hidden = slices.filter((s) => s.value > 0 && off.has(s.key)).length;
  const current = slices.find((s) => s.key === active);
  // The hole is 62% of the ring's width; text must fit inside 90% of it (a glyph is about 0.6em wide)
  // at no less than 11px. A swapped-in figure that cannot is not swapped in: the headline stays, and
  // the legend and the arc's name carry the figure. No number is ever wider than the hole.
  const hole = size * 0.62;
  const fitOf = (text: string) => (0.9 * hole) / (0.6 * Math.max(1, text.length));
  const swaps = current !== undefined && fitOf(current.text) >= 11;
  const shownCentre = swaps ? current.text : centre;
  const fit = Math.min(20, fitOf(shownCentre));

  if (asTable) {
    return (
      <div className={styles.donut} data-testid={testId}>
        <ChartTable
          visible
          caption={caption}
          head={["Part", "Figure", "Share"]}
          rows={slices.map((s) => [s.label, s.text, s.share ?? ""])}
          note={`Total: ${centre}${centreLabel ? ` ${centreLabel}` : ""}`}
        />
      </div>
    );
  }

  return (
    <div className={styles.donut} data-testid={testId}>
      <motion.div
        className={styles.ring}
        style={{ width: size, height: size }}
        initial={reduce ? false : { opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduce ? { duration: 0 } : { duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <svg width={size} height={size} role="group" aria-label={caption}>
          <Group top={radius} left={radius}>
            <Pie
              data={shown}
              pieValue={(s) => s.value}
              outerRadius={radius - PULL - 1}
              innerRadius={radius * 0.62}
              padAngle={0.012}
              pieSortValues={() => 0}
            >
              {(pie) =>
                pie.arcs.map((arc) => {
                  const slice = arc.data;
                  const mid = (arc.startAngle + arc.endAngle) / 2;
                  const out = active === slice.key;
                  const open = receiptProps(slice.receipt, slice.key, onOpenReceipt);
                  return (
                    <path
                      key={slice.key}
                      d={pie.path(arc) ?? ""}
                      tabIndex={0}
                      role="img"
                      aria-label={`${sentence(slice)}${canOpen(slice.receipt, onOpenReceipt) ? ". Opens the receipt" : ""}`}
                      className={`${styles.arc} ${active && active !== slice.key ? styles.dim : ""} ${open.onClick ? styles.openable : ""}`}
                      style={{
                        fill: colourOf(slice.colour),
                        transform: out
                          ? `translate(${(Math.sin(mid) * PULL).toFixed(2)}px, ${(-Math.cos(mid) * PULL).toFixed(2)}px)`
                          : undefined,
                      }}
                      data-testid="donut-arc"
                      data-slice={slice.key}
                      data-out={out ? "true" : undefined}
                      data-receipt={open["data-receipt"]}
                      onClick={open.onClick}
                      onKeyDown={(event: KeyboardEvent) => {
                        if (event.key === "Escape") setActive(null);
                        else open.onKeyDown?.(event);
                      }}
                      onFocus={() => {
                        setActive(slice.key);
                      }}
                      onBlur={() => {
                        setActive(null);
                      }}
                      onPointerEnter={() => {
                        setActive(slice.key);
                      }}
                      onPointerLeave={() => {
                        setActive(null);
                      }}
                    />
                  );
                })
              }
            </Pie>
          </Group>
        </svg>
        <div className={styles.centre}>
          <span className={`${styles.centreValue} num`} data-chart-centre="" style={{ fontSize: fit }}>
            {shownCentre}
          </span>
          {swaps ? (
            <span className={styles.centreLabel}>{current.label}</span>
          ) : centreLabel ? (
            <span className={styles.centreLabel}>{centreLabel}</span>
          ) : null}
          {!swaps && hidden > 0 ? <span className={styles.centreLabel}>{String(hidden)} hidden</span> : null}
        </div>
      </motion.div>
      <ul className={styles.legend} data-testid="donut-legend">
        {slices.map((s) => {
          const isOff = off.has(s.key);
          const row = (
            <>
              <span
                className={styles.dotMark}
                aria-hidden="true"
                style={{ background: isOff ? "transparent" : colourOf(s.colour), borderColor: colourOf(s.colour) }}
              />
              <span className={styles.itemLabel}>{s.label}</span>
              <span className={`${styles.itemValue} num`}>{s.text}</span>
              {s.share ? <span className={`${styles.itemShare} num`}>{s.share}</span> : null}
            </>
          );
          return (
            <li
              key={s.key}
              className={`${styles.item} ${active === s.key ? styles.itemOn : ""} ${isOff ? styles.itemOff : ""}`}
              onPointerEnter={() => {
                if (!isOff) setActive(s.key);
              }}
              onPointerLeave={() => {
                setActive(null);
              }}
            >
              {s.value > 0 && toggleable ? (
                <button
                  type="button"
                  className={styles.toggle}
                  aria-pressed={!isOff}
                  aria-label={`${sentence(s)}. ${isOff ? "Hidden" : "Shown"}`}
                  onClick={() => {
                    toggle(s.key);
                  }}
                >
                  {row}
                </button>
              ) : s.value > 0 && canOpen(s.receipt, onOpenReceipt) ? (
                // A legend row that prints a figure opens its receipt like its arc does (rule 3, G-P7-10).
                <button
                  type="button"
                  className={styles.toggle}
                  data-receipt={s.receipt}
                  data-testid="donut-legend-open"
                  aria-label={`${sentence(s)}. Show the figures behind it`}
                  onClick={() => {
                    if (s.receipt) onOpenReceipt?.(s.receipt, s.key);
                  }}
                >
                  {row}
                </button>
              ) : (
                <span className={styles.toggle}>{row}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
